import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FiPlus, FiX } from 'react-icons/fi';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { selectSelectedLeague, selectSelectedLeagueId } from '../../../redux/slice/selectedLeagueSlice';
import { setActiveTab } from '../../../redux/slice/activeTabSlice';
import { QUERY_KEY } from '../../../services/queryEndPoint';
import { canManageWaiversInLeague } from '../../../utils/leagueAccess';
import { ProfileDatePicker } from '../../profileOnboarding/components/ProfileDatePicker';
import RichTextEditor from './components/RichTextEditor';
import {
  PLAYER_INFO_FIELDS,
  createWaiver,
  fetchWaiver,
  updateWaiver,
  type IWaiverRequest,
} from '../tournaments/api/waiverApi';
import './CreateWaiverPage.scss';

const DEFAULT_AGREE_LABEL = 'I have read and agree to terms and conditions';

interface WaiverFormState {
  name: string;
  description: string;
  effectiveFromDate: string;
  expiryDate: string;
  playerInfoFields: string[];
  customFields: string[];
  agreementContent: string;
  requireAgreeCheckbox: boolean;
  agreeCheckboxLabel: string;
  requireDigitalSignature: boolean;
  requireDateConfirmation: boolean;
  requireParentGuardianIfMinor: boolean;
}

const emptyForm = (): WaiverFormState => ({
  name: '',
  description: '',
  effectiveFromDate: '',
  expiryDate: '',
  playerInfoFields: PLAYER_INFO_FIELDS.map((f) => f.value),
  customFields: [],
  agreementContent: '',
  requireAgreeCheckbox: true,
  agreeCheckboxLabel: DEFAULT_AGREE_LABEL,
  requireDigitalSignature: false,
  requireDateConfirmation: false,
  requireParentGuardianIfMinor: false,
});

type FormErrors = Partial<Record<keyof WaiverFormState, string>>;

function validate(form: WaiverFormState): FormErrors {
  const errors: FormErrors = {};
  if (!form.name.trim()) errors.name = 'Waiver name is required';
  else if (form.name.trim().length > 255) errors.name = 'Must be at most 255 characters';

  if (!form.description.trim()) errors.description = 'Waiver description is required';
  else if (form.description.trim().length > 1000) errors.description = 'Must be at most 1000 characters';

  if (!form.effectiveFromDate) errors.effectiveFromDate = 'Effective from date is required';

  if (form.expiryDate && form.effectiveFromDate && form.expiryDate < form.effectiveFromDate) {
    errors.expiryDate = 'Expiry date cannot be before the effective from date';
  }

  const plainText = form.agreementContent.replace(/<[^>]*>/g, '').trim();
  if (!plainText) errors.agreementContent = 'Waiver agreement content is required';

  if (form.requireAgreeCheckbox && !form.agreeCheckboxLabel.trim()) {
    errors.agreeCheckboxLabel = 'Checkbox label is required';
  }

  return errors;
}

function getWaiverErrorMessage(error: unknown, fallback: string): string {
  const response = (error as { response?: { data?: unknown } })?.response;
  const data = response?.data;
  if (data && typeof data === 'object' && 'message' in data) {
    const message = (data as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  if (typeof data === 'string' && data.trim()) return data;
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}

export default function CreateWaiverPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { waiverId } = useParams<{ waiverId: string }>();
  const isEditMode = !!waiverId;

  const selectedLeague = useAppSelector(selectSelectedLeague);
  const selectedLeagueId = useAppSelector(selectSelectedLeagueId);

  const [form, setForm] = useState<WaiverFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [customFieldDraft, setCustomFieldDraft] = useState('');
  const [showCustomFieldInput, setShowCustomFieldInput] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const { data: existingWaiver, isLoading: isLoadingWaiver } = useQuery({
    queryKey: [QUERY_KEY.leagueWaivers, selectedLeagueId, 'waiver', waiverId],
    queryFn: () => fetchWaiver(selectedLeagueId as string, waiverId as string),
    enabled: isEditMode && !!selectedLeagueId && !!waiverId,
  });

  useEffect(() => {
    if (!existingWaiver) return;
    setForm({
      name: existingWaiver.name ?? '',
      description: existingWaiver.description ?? '',
      effectiveFromDate: existingWaiver.effectiveFromDate ?? '',
      expiryDate: existingWaiver.expiryDate ?? '',
      playerInfoFields: existingWaiver.playerInfoFields ?? [],
      customFields: existingWaiver.customFields ?? [],
      agreementContent: existingWaiver.agreementContent ?? '',
      requireAgreeCheckbox: existingWaiver.requireAgreeCheckbox,
      agreeCheckboxLabel: existingWaiver.agreeCheckboxLabel || DEFAULT_AGREE_LABEL,
      requireDigitalSignature: existingWaiver.requireDigitalSignature,
      requireDateConfirmation: existingWaiver.requireDateConfirmation,
      requireParentGuardianIfMinor: existingWaiver.requireParentGuardianIfMinor,
    });
    setResetKey((k) => k + 1);
  }, [existingWaiver]);

  const goToWaiverTab = () => {
    dispatch(setActiveTab('waiver'));
    sessionStorage.setItem('activeTab', 'waiver');
    navigate(selectedLeagueId ? `/my-leagues/${selectedLeagueId}` : '/my-leagues');
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedLeagueId) throw new Error('League is missing.');
      const request: IWaiverRequest = {
        name: form.name.trim(),
        description: form.description.trim(),
        effectiveFromDate: form.effectiveFromDate,
        expiryDate: form.expiryDate || null,
        agreementContent: form.agreementContent,
        playerInfoFields: form.playerInfoFields,
        customFields: form.customFields,
        requireAgreeCheckbox: form.requireAgreeCheckbox,
        agreeCheckboxLabel: form.agreeCheckboxLabel.trim() || DEFAULT_AGREE_LABEL,
        requireDigitalSignature: form.requireDigitalSignature,
        requireDateConfirmation: form.requireDateConfirmation,
        requireParentGuardianIfMinor: form.requireParentGuardianIfMinor,
      };
      return isEditMode
        ? updateWaiver(selectedLeagueId, waiverId as string, request)
        : createWaiver(selectedLeagueId, request);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.leagueWaivers, selectedLeagueId] });
      toast.success(isEditMode ? 'Waiver updated successfully.' : 'Waiver created successfully.');
      goToWaiverTab();
    },
    onError: (error) => {
      toast.error(getWaiverErrorMessage(error, isEditMode ? 'Failed to update waiver.' : 'Failed to create waiver.'));
    },
  });

  const toggleFixedField = (value: string) => {
    setForm((prev) => ({
      ...prev,
      playerInfoFields: prev.playerInfoFields.includes(value)
        ? prev.playerInfoFields.filter((v) => v !== value)
        : [...prev.playerInfoFields, value],
    }));
  };

  const addCustomField = () => {
    const label = customFieldDraft.trim();
    if (!label) return;
    if (!form.customFields.includes(label)) {
      setForm((prev) => ({ ...prev, customFields: [...prev.customFields, label] }));
    }
    setCustomFieldDraft('');
    setShowCustomFieldInput(false);
  };

  const removeCustomField = (label: string) => {
    setForm((prev) => ({ ...prev, customFields: prev.customFields.filter((f) => f !== label) }));
  };

  const handleSave = () => {
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      toast.error('Please fix the highlighted fields.');
      return;
    }
    saveMutation.mutate();
  };

  const canManage = useMemo(() => canManageWaiversInLeague(selectedLeague), [selectedLeague]);

  if (!canManage) {
    return (
      <div className="create-waiver-wrapper custom-container">
        <p className="create-waiver-unauthorized">
          Only league admins can create or edit waivers.
        </p>
      </div>
    );
  }

  if (isEditMode && isLoadingWaiver) {
    return (
      <div className="create-waiver-wrapper custom-container">
        <p>Loading waiver…</p>
      </div>
    );
  }

  return (
    <div className="create-waiver-wrapper custom-container">
      <button type="button" className="create-waiver-back" onClick={goToWaiverTab}>
        <span aria-hidden>←</span>
        {isEditMode ? 'Edit Waiver' : 'Create New Waiver'}
      </button>

      <section className="create-waiver-section">
        <div className="create-waiver-section-header">
          <h2>Waiver Information</h2>
          <span className="create-waiver-section-line" />
        </div>
        <div className="create-waiver-row">
          <div className="create-waiver-field">
            <label>
              Waiver Name<span className="req">*</span>
            </label>
            <input
              type="text"
              placeholder='Example: "FCA General Liability Waiver 2025"'
              value={form.name}
              maxLength={255}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              className={errors.name ? 'field-error' : ''}
            />
            {errors.name && <p className="err-msg">{errors.name}</p>}
          </div>
          <div className="create-waiver-field">
            <label>
              Waiver Description<span className="req">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter a short description"
              value={form.description}
              maxLength={1000}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              className={errors.description ? 'field-error' : ''}
            />
            {errors.description && <p className="err-msg">{errors.description}</p>}
          </div>
        </div>
        <div className="create-waiver-row">
          <div className="create-waiver-field">
            <label>
              Effective From Date<span className="req">*</span>
            </label>
            <ProfileDatePicker
              id="waiver-effective-from-date"
              value={form.effectiveFromDate}
              onChange={(value) => setForm((p) => ({ ...p, effectiveFromDate: value }))}
              placeholder="Start Date"
              hasError={!!errors.effectiveFromDate}
            />
            {errors.effectiveFromDate && <p className="err-msg">{errors.effectiveFromDate}</p>}
          </div>
          <div className="create-waiver-field">
            <label>Expiry (Optional)</label>
            <ProfileDatePicker
              id="waiver-expiry-date"
              value={form.expiryDate}
              onChange={(value) => setForm((p) => ({ ...p, expiryDate: value }))}
              placeholder="End Date"
              hasError={!!errors.expiryDate}
            />
            {errors.expiryDate && <p className="err-msg">{errors.expiryDate}</p>}
          </div>
        </div>
      </section>

      <section className="create-waiver-section">
        <div className="create-waiver-section-header">
          <h2>Player Information Fields</h2>
          <span className="create-waiver-section-line" />
        </div>
        <div className="create-waiver-checkbox-grid">
          {PLAYER_INFO_FIELDS.map((field) => (
            <label className="waiver-checkbox" key={field.value}>
              <input
                type="checkbox"
                checked={form.playerInfoFields.includes(field.value)}
                onChange={() => toggleFixedField(field.value)}
              />
              {field.label}
            </label>
          ))}
          {form.customFields.map((label) => (
            <span className="waiver-checkbox waiver-checkbox--custom" key={label}>
              <label>
                <input type="checkbox" checked readOnly />
                {label}
              </label>
              <button
                type="button"
                className="waiver-custom-field-remove"
                onClick={() => removeCustomField(label)}
                aria-label={`Remove ${label}`}
              >
                <FiX size={14} />
              </button>
            </span>
          ))}
        </div>
        {showCustomFieldInput ? (
          <div className="create-waiver-custom-field-input">
            <input
              type="text"
              placeholder="Custom field name"
              value={customFieldDraft}
              maxLength={120}
              autoFocus
              onChange={(e) => setCustomFieldDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCustomField();
                } else if (e.key === 'Escape') {
                  setShowCustomFieldInput(false);
                  setCustomFieldDraft('');
                }
              }}
            />
            <button type="button" className="waiver-btn waiver-btn--secondary" onClick={addCustomField}>
              Add
            </button>
            <button
              type="button"
              className="waiver-btn waiver-btn--text"
              onClick={() => {
                setShowCustomFieldInput(false);
                setCustomFieldDraft('');
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="create-waiver-add-field-btn"
            onClick={() => setShowCustomFieldInput(true)}
          >
            <FiPlus size={16} />
            Add Custom Field
          </button>
        )}
      </section>

      <section className="create-waiver-section">
        <div className="create-waiver-section-header">
          <h2>Legal Agreement Content</h2>
          <span className="create-waiver-section-line" />
        </div>
        <div className="create-waiver-field">
          <label>
            Waiver Agreement<span className="req">*</span>
          </label>
          <RichTextEditor
            initialValue={form.agreementContent}
            onChange={(html) => setForm((p) => ({ ...p, agreementContent: html }))}
            placeholder="Enter the waiver agreement text players will read and sign…"
            hasError={!!errors.agreementContent}
            resetKey={resetKey}
          />
          {errors.agreementContent && <p className="err-msg">{errors.agreementContent}</p>}
        </div>
        <div className="create-waiver-row create-waiver-row--checkboxes">
          <label className="waiver-checkbox">
            <input
              type="checkbox"
              checked={form.requireAgreeCheckbox}
              onChange={(e) => setForm((p) => ({ ...p, requireAgreeCheckbox: e.target.checked }))}
            />
            Require 'I Agree' Checkbox
          </label>
          {form.requireAgreeCheckbox && (
            <div className="create-waiver-field create-waiver-agree-label">
              <label>Checkbox label shown to players</label>
              <input
                type="text"
                value={form.agreeCheckboxLabel}
                maxLength={500}
                onChange={(e) => setForm((p) => ({ ...p, agreeCheckboxLabel: e.target.value }))}
                className={errors.agreeCheckboxLabel ? 'field-error' : ''}
              />
              {errors.agreeCheckboxLabel && <p className="err-msg">{errors.agreeCheckboxLabel}</p>}
            </div>
          )}
        </div>
      </section>

      <section className="create-waiver-section">
        <div className="create-waiver-section-header">
          <h2>Signature Requirements</h2>
          <span className="create-waiver-section-line" />
        </div>
        <div className="create-waiver-row create-waiver-row--checkboxes">
          <label className="waiver-checkbox">
            <input
              type="checkbox"
              checked={form.requireDigitalSignature}
              onChange={(e) => setForm((p) => ({ ...p, requireDigitalSignature: e.target.checked }))}
            />
            Require Digital Signature
          </label>
          <label className="waiver-checkbox">
            <input
              type="checkbox"
              checked={form.requireDateConfirmation}
              onChange={(e) => setForm((p) => ({ ...p, requireDateConfirmation: e.target.checked }))}
            />
            Require Date Confirmation
          </label>
          <label className="waiver-checkbox">
            <input
              type="checkbox"
              checked={form.requireParentGuardianIfMinor}
              onChange={(e) => setForm((p) => ({ ...p, requireParentGuardianIfMinor: e.target.checked }))}
            />
            Require Parent/Guardian if under 18
          </label>
        </div>
      </section>

      <div className="create-waiver-actions">
        <button
          type="button"
          className="waiver-btn waiver-btn--primary"
          onClick={handleSave}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? 'Saving…' : 'Save Waiver'}
        </button>
      </div>
    </div>
  );
}
